import React from 'react'

import { createCheckoutSession, syncContract, checkoutCompleted } from '../../api/subscription/actions'
import { loadAskell, currentColorScheme, defaultCurrency, widgetLanguage } from '../../api/subscription/askell'
import { fetchUser } from '../../api/user/actions'
import { setStep } from '../../api/step-renderer/actions'

const CURRENCIES = [
  { code: 'ISK', label: 'ISK (kr.)' },
  { code: 'USD', label: 'USD ($)' },
]

/*
 * Áskell embedded checkout (Subscription Contracts V2).
 *
 * 1. Ask the Guitarparty backend for a checkout session on the sales
 *    channel for the chosen currency (guitarparty-isk / guitarparty-usd).
 *    The backend also snapshots a Guitarparty theme on the session, light
 *    or dark depending on the user's dark mode setting.
 * 2. Mount askell.js in this component with the session token.
 * 3. When the widget reports success, have the backend verify the contract
 *    with Áskell, refresh the user and move to the "done" step.
 */
class AskellCheckout extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      currency: props.currency || defaultCurrency(),
      colorScheme: currentColorScheme(),
      status: 'loading',   // loading | ready | error | blocked | success
      error: null,
    }

    this.container = React.createRef()
    this.widget = null
    this.mountId = 0

    this.mountWidget = this.mountWidget.bind(this)
    this.destroyWidget = this.destroyWidget.bind(this)
    this.setCurrency = this.setCurrency.bind(this)
    this.onSuccess = this.onSuccess.bind(this)
    this.onError = this.onError.bind(this)
  }

  componentDidMount() {
    this.mountWidget()
  }

  componentWillUnmount() {
    this.mountId += 1
    this.destroyWidget()
  }

  destroyWidget() {
    if (this.widget) {
      try {
        this.widget.destroy()
      } catch (e) {
        void(e)
      }
      this.widget = null
    }
  }

  mountWidget() {
    const mountId = ++this.mountId
    const currency = this.state.currency
    const colorScheme = currentColorScheme()

    this.destroyWidget()
    this.setState({ status: 'loading', error: null, colorScheme: colorScheme })

    Promise.all([
      this.props.dispatch(createCheckoutSession(currency, colorScheme, this.props.source)),
      loadAskell(),
    ])
    .then(([session, Askell]) => {
      if (mountId !== this.mountId || !this.container.current) {
        return
      }
      let options = {
        sessionToken: session.token,
        language: widgetLanguage(),
        colorScheme: session.color_scheme || colorScheme,
        title: gettext('Choose your subscription'),
        description: gettext('Pick a plan and enter your card details. You can stop the renewal at any time on the subscription page.'),
        onSuccess: this.onSuccess,
        onError: this.onError,
      }
      if (window.ASKELL_WIDGET_BASE_URL && window.ASKELL_WIDGET_BASE_URL.indexOf('askell.is') === -1) {
        // Development against a local Áskell instance
        options.baseUrl = window.ASKELL_WIDGET_BASE_URL
      }
      this.widget = Askell.mountCheckout(this.container.current, options)
      this.setState({ status: 'ready' })
    })
    .catch(error => {
      if (mountId !== this.mountId) {
        return
      }
      if (error && error.status === 409) {
        // Already a subscriber (one subscription per user), e.g. a checkout
        // that finished in another tab. Refresh the user so the modal
        // shows the subscription instead of the checkout.
        this.setState({ status: 'blocked', error: error.message })
        this.props.dispatch(fetchUser())
        return
      }
      this.setState({
        status: 'error',
        error: (error && error.message) || gettext('Could not start checkout. Please try again.'),
      })
    })
  }

  setCurrency(currency) {
    if (currency === this.state.currency || this.state.status === 'success') {
      return
    }
    this.setState({ currency: currency }, this.mountWidget)
  }

  onSuccess(result) {
    let contractId = null
    if (result) {
      contractId = result.contract_id || (result.contract && result.contract.id) || null
    }
    this.setState({ status: 'success' })
    this.props.dispatch(checkoutCompleted(result))

    // Access is only granted once our backend has verified the contract
    // with Áskell. If that call fails the webhook will catch up shortly.
    Promise.resolve(this.props.dispatch(syncContract(contractId)))
      .catch(() => null)
      .then(() => this.props.dispatch(fetchUser()))
      .then(() => this.props.dispatch(setStep(1)))
  }

  onError(error) {
    // The widget shows the error itself; keep a trace for support.
    if (window.console) {
      console.error('Askell checkout error', error)
    }
  }

  render() {
    const { currency, colorScheme, status, error } = this.state

    return (
      <div className={`askell-checkout scheme-${colorScheme}`}>
        {status !== 'success' && status !== 'blocked' &&
          <div className="row justify-content-center subscription-interval askell-checkout-currency">
            {CURRENCIES.map(item =>
              <div key={item.code} className="col-md-4 col-8 text-center">
                <a className={item.code === currency ? 'active' : ''} onClick={() => this.setCurrency(item.code)}>{item.label}</a>
              </div>
            )}
          </div>
        }
        {status === 'loading' &&
          <div className="askell-checkout-loading">
            <i className="fa fa-circle-o-notch fa-spin fa-fw"></i> {gettext('Loading checkout…')}
          </div>
        }
        {status === 'error' &&
          <div className="alert alert-danger askell-checkout-error">
            <span>{error}</span>
            <button type="button" className="btn btn-outline-light" onClick={this.mountWidget}>{gettext('Try again')}</button>
          </div>
        }
        {status === 'blocked' &&
          <div className="alert alert-info askell-checkout-error askell-checkout-blocked">
            <span>{error}</span>
          </div>
        }
        <div className="askell-checkout-mount" ref={this.container}></div>
      </div>
    )
  }
}

AskellCheckout.defaultProps = {
  source: 'web',
}

export default AskellCheckout
